// src/hooks/useElectronData.js
import { useEffect, useRef } from 'react'
import { useDataStore } from '../stores/dataStore'
import { mockElectronAPI } from '../utils/mockElectron'
import {
  playConnectionSound,
  playAlertSound,
  playCriticalSound,
  playStartupSound,
  isSoundEnabled,
} from '../utils/sounds'

// Use real Electron API if available, otherwise mock
const api = typeof window !== 'undefined' && window.electronAPI
  ? window.electronAPI
  : mockElectronAPI

export function useElectronData() {
  const {
    setSpeed, setConnections, setMetrics, addAlert, dismissAlert,
    soundEnabled, setSoundEnabled,
  } = useDataStore()

  const initialFetchDone  = useRef(false)
  const prevConnCount     = useRef(0)

  // Sync sound enabled state from localStorage on mount
  useEffect(() => {
    setSoundEnabled(isSoundEnabled())
  }, [])

  useEffect(() => {
    if (!initialFetchDone.current) {
      initialFetchDone.current = true

      api.getConnections().then(conns => {
        setConnections(conns)
        prevConnCount.current = conns.length
        // Gentle startup chime
        setTimeout(() => playStartupSound(), 600)
      }).catch(console.error)

      api.getSystemMetrics().then(setMetrics).catch(console.error)
      api.getAlertHistory().then(alerts => {
        alerts.forEach(a => addAlert(a))
      }).catch(console.error)
    }

    const cleanups = [
      api.onSpeedUpdate(setSpeed),

      api.onConnectionsUpdate(conns => {
        const prev = prevConnCount.current
        const next = conns.length
        // Play connection sound when a new connection appears
        if (next > prev) playConnectionSound()
        prevConnCount.current = next
        setConnections(conns)
      }),

      api.onMetricsUpdate(setMetrics),

      api.onNewAlert(alert => {
        addAlert(alert)
        // Different sound based on severity
        if (alert.severity === 'critical') playCriticalSound()
        else playAlertSound()
      }),
      
      api.onAlertDismissed((id) => {
        dismissAlert(id)
      }),
    ]

    return () => cleanups.forEach(fn => fn && fn())
  }, [])

  const handleDismissAlert = async (alertId) => {
    dismissAlert(alertId)
    try { await api.dismissAlert(alertId) } catch (_) {}
  }

  return { dismissAlert: handleDismissAlert }
}
